import React, { useState, useEffect, useRef, useCallback } from 'react';
import { collection, query, where, orderBy, onSnapshot, addDoc, doc, updateDoc } from 'firebase/firestore';

// ClientSupport Component
const ClientSupport = ({ user, db, setModalMessage, fetchedUsername, onShowNewTicketModal }) => {
    console.count('ClientSupport Render'); // DEBUG: ClientSupport render count
    const [tickets, setTickets] = useState([]);
    const [selectedTicket, setSelectedTicket] = useState(null);
    // showNewTicketModal state removed - now handled by App.js
    // newMessage state moved to TicketDetail

    const messagesEndRef = useRef(null); // Ref for scrolling to bottom of messages

    // Fetch user's support tickets
    useEffect(() => {
        if (!user || !db) return;

        const ticketsCollectionRef = collection(db, 'supportTickets');
        // Query tickets where userId matches the current user's UID, ordered by lastUpdated
        const q = query(ticketsCollectionRef, where('userId', '==', user.uid), orderBy('lastUpdated', 'desc'));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const fetchedTickets = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                createdAt: doc.data().createdAt?.toDate().toLocaleString(), // Convert Firestore Timestamp to readable string
                lastUpdated: doc.data().lastUpdated?.toDate().toLocaleString() // Convert Firestore Timestamp to readable string
            }));
            setTickets(fetchedTickets);
        }, (error) => {
            console.error("Error fetching client support tickets:", error);
            setModalMessage(`Error loading your tickets: ${error.message}`);
        });

        return () => unsubscribe();
    }, [user, db, setModalMessage]);

    // Fetch messages for the selected ticket
    useEffect(() => {
        if (!selectedTicket || !db) return;

        const messagesCollectionRef = collection(db, `supportTickets/${selectedTicket.id}/messages`);
        const q = query(messagesCollectionRef, orderBy('timestamp', 'asc'));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const fetchedMessages = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                timestamp: doc.data().timestamp?.toDate().toLocaleString() // Convert Firestore Timestamp
            }));
            // Update only the messages array of the selected ticket, keeping other properties stable
            setSelectedTicket(prev => {
                if (!prev) return null;
                // Only update messages if they are different to avoid unnecessary renders of selectedTicket
                if (JSON.stringify(prev.messages) === JSON.stringify(fetchedMessages)) {
                    return prev;
                }
                return { ...prev, messages: fetchedMessages };
            });
        }, (error) => {
            console.error("Error fetching ticket messages:", error);
            setModalMessage(`Error loading messages: ${error.message}`);
        });

        return () => unsubscribe();
    }, [selectedTicket?.id, db, setModalMessage]);

    // Scroll to bottom of messages when they load or update
    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [selectedTicket?.messages]);

    // handleCreateNewTicket function removed - now handled by App.js

    // handleSendMessage now accepts messageText from TicketDetail
    const handleSendMessage = useCallback(async (messageText) => {
        if (!messageText.trim() || !selectedTicket || !user || !db) {
            setModalMessage("Message cannot be empty.");
            return;
        }

        try {
            const messagesCollectionRef = collection(db, `supportTickets/${selectedTicket.id}/messages`);
            await addDoc(messagesCollectionRef, {
                senderId: user.uid,
                senderName: fetchedUsername || user.email,
                text: messageText.trim(), // Use messageText received from prop
                timestamp: new Date(), // Firestore Timestamp
            });

            // Update lastUpdated field on the parent ticket
            const ticketDocRef = doc(db, 'supportTickets', selectedTicket.id);
            await updateDoc(ticketDocRef, {
                lastUpdated: new Date()
            });

            // setNewMessage('') is now handled by TicketDetail
        } catch (error) {
            console.error("Error sending message:", error);
            setModalMessage(`Failed to send message: ${error.message}`);
        }
    }, [selectedTicket, user, db, setModalMessage, fetchedUsername]);

    const handleBackToTickets = useCallback(() => {
        // newMessage is now handled by TicketDetail
        setSelectedTicket(null); // Unselect ticket to show list
    }, []);

    const TicketList = () => (
        <div className="glass-card rounded-2xl h-full flex flex-col p-8 animate-fadeIn">
            <div className="flex items-center justify-between mb-8 animate-slideInLeft">
                <h2 className="text-3xl font-bold gradient-text flex items-center">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border border-blue-500/30 flex items-center justify-center mr-4 animate-heartbeat">
                        <i className="fas fa-headset text-blue-400"></i>
                    </div>
                    Support Center
                </h2>
                <button
                    onClick={onShowNewTicketModal}
                    className="glass-card backdrop-blur-xl border-2 border-blue-400/60 px-6 py-3 rounded-2xl font-semibold text-white shadow-xl hover:shadow-2xl hover:shadow-blue-500/25 transition-all duration-500 hover:scale-105 hover:-translate-y-1 relative overflow-hidden group bg-gradient-to-br from-blue-500/40 to-cyan-500/30"
                >
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
                    <div className="relative z-10 flex items-center">
                        <i className="fas fa-plus mr-2 transition-all duration-300 group-hover:rotate-90 group-hover:scale-110"></i>
                        New Ticket
                    </div>
                </button>
            </div>
            
            <div className="flex-1 overflow-y-auto custom-scrollbar">
                {tickets.length === 0 ? (
                    <div className="glass-card rounded-2xl p-8 text-center animate-bounceIn transform hover:scale-[1.02] transition-all duration-500 hover:shadow-2xl hover:shadow-blue-500/10 relative overflow-hidden group" style={{ animationDelay: '0.2s' }}>
                        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-cyan-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                        <div className="relative z-10">
                            <div className="w-24 h-24 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border border-blue-500/30 flex items-center justify-center animate-float">
                                <i className="fas fa-ticket-alt text-4xl text-blue-400"></i>
                            </div>
                            <h3 className="text-2xl font-bold text-white mb-3 transition-colors duration-300 group-hover:text-blue-100">No Support Tickets</h3>
                            <p className="text-gray-400 mb-8 text-lg transition-colors duration-300 group-hover:text-gray-300">Create your first ticket to get assistance from our support team</p>
                            <button
                                onClick={onShowNewTicketModal}
                                className="glass-card backdrop-blur-xl border-2 border-blue-400/60 px-8 py-4 rounded-2xl font-semibold text-white shadow-xl hover:shadow-2xl hover:shadow-blue-500/25 transition-all duration-500 hover:scale-110 hover:-translate-y-2 relative overflow-hidden group/btn bg-gradient-to-br from-blue-500/40 to-cyan-500/30"
                            >
                                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent translate-x-[-100%] group-hover/btn:translate-x-[100%] transition-transform duration-1000"></div>
                                <div className="relative z-10 flex items-center">
                                    <i className="fas fa-plus mr-3 transition-all duration-300 group-hover/btn:rotate-90 group-hover/btn:scale-110"></i>
                                    Create First Ticket
                                </div>
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-6">
                        {tickets.map((ticket, index) => (
                            <div
                                key={ticket.id}
                                className="glass-card rounded-2xl p-6 cursor-pointer hover:bg-white/10 transition-all duration-500 hover:scale-[1.03] hover:-translate-y-2 hover:shadow-2xl hover:shadow-blue-500/20 group relative overflow-hidden animate-fadeIn border border-white/10 hover:border-blue-400/50"
                                style={{ animationDelay: `${index * 0.1 + 0.2}s` }}
                                onClick={() => setSelectedTicket(ticket)}
                            >
                                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-400/5 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
                                <div className="flex justify-between items-start mb-4 relative z-10">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-4 mb-3">
                                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border border-blue-500/30 flex items-center justify-center transition-all duration-300 group-hover:scale-110 group-hover:rotate-3">
                                                <i className="fas fa-ticket-alt text-blue-400 transition-colors duration-300 group-hover:text-blue-300"></i>
                                            </div>
                                            <h3 className="text-lg font-bold text-white group-hover:text-blue-100 transition-all duration-300 group-hover:translate-x-1">
                                                {ticket.subject}
                                            </h3>
                                        </div>
                                        <p className="text-gray-300 text-sm line-clamp-2 mb-4 transition-colors duration-300 group-hover:text-gray-200 group-hover:translate-x-1">{ticket.description}</p>
                                        <div className="flex items-center gap-6 text-sm text-gray-400 transition-all duration-300 group-hover:text-gray-300 group-hover:translate-x-1">
                                            <span className="flex items-center gap-2">
                                                <i className="fas fa-calendar-alt text-blue-400"></i> 
                                                {ticket.lastUpdated}
                                            </span>
                                            <span className="flex items-center gap-2">
                                                <i className="fas fa-building text-green-400"></i> 
                                                {ticket.department}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex flex-col items-end gap-3">
                                        <span className={`px-4 py-2 rounded-2xl text-xs font-bold transition-all duration-500 shadow-lg ${
                                            ticket.status === 'open' 
                                                ? 'bg-gradient-to-br from-green-500/30 to-emerald-500/20 text-green-300 border-2 border-green-400/50 group-hover:shadow-green-500/25' 
                                                : 'bg-gradient-to-br from-gray-500/30 to-gray-600/20 text-gray-300 border-2 border-gray-400/50 group-hover:shadow-gray-500/25'
                                        } group-hover:scale-105`}>
                                            {ticket.status.toUpperCase()}
                                        </span>
                                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border border-blue-400/30 flex items-center justify-center transition-all duration-500 group-hover:scale-110 group-hover:bg-gradient-to-br group-hover:from-blue-500/40 group-hover:to-cyan-500/30">
                                            <i className="fas fa-arrow-right text-blue-400 group-hover:text-blue-300 group-hover:translate-x-1 transition-all duration-300"></i>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );

    // TicketDetail now manages its own newMessage state
    const TicketDetail = React.memo(({ selectedTicket, user, handleSendMessage, messagesEndRef, handleBackToTickets }) => {
        console.count('TicketDetail Render Count'); // DEBUG: TicketDetail render count
        const [newMessage, setNewMessage] = useState(''); // newMessage state is local to TicketDetail

        const handleLocalSendMessage = useCallback(() => {
            handleSendMessage(newMessage); // Pass local newMessage to parent handler
            setNewMessage(''); // Clear local input after sending
        }, [newMessage, handleSendMessage]);

        const handleTextareaChange = useCallback((e) => {
            console.log('Textarea: onChange fired. New value:', e.target.value); // DEBUG
            setNewMessage(e.target.value);
        }, []);


        return (
            <div className="glass-card rounded-2xl h-full flex flex-col p-8 animate-fadeIn">
                <div className="flex justify-between items-center mb-8 animate-slideInLeft">
                    <button
                        onClick={handleBackToTickets}
                        className="glass-card backdrop-blur-xl border-2 border-white/20 px-6 py-3 rounded-2xl hover:bg-white/10 text-white font-semibold transition-all duration-500 hover:scale-105 hover:-translate-y-1 hover:shadow-xl hover:shadow-gray-500/20 relative overflow-hidden group bg-white/5"
                    >
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
                        <div className="relative z-10 flex items-center">
                            <i className="fas fa-arrow-left mr-2 transition-all duration-300 group-hover:-translate-x-1 group-hover:scale-110"></i>
                            Back to Tickets
                        </div>
                    </button>
                    <div className="text-center flex-1 animate-bounceIn" style={{ animationDelay: '0.2s' }}>
                        <h2 className="text-2xl font-bold gradient-text flex items-center justify-center">
                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border border-blue-500/30 flex items-center justify-center mr-3 animate-heartbeat">
                                <i className="fas fa-ticket-alt text-blue-400"></i>
                            </div>
                            {selectedTicket.subject}
                        </h2>
                    </div>
                    <span className={`px-6 py-3 rounded-2xl text-sm font-bold transition-all duration-500 shadow-xl animate-fadeIn ${
                        selectedTicket.status === 'open' 
                            ? 'bg-gradient-to-br from-green-500/30 to-emerald-500/20 text-green-300 border-2 border-green-400/50 shadow-green-500/25' 
                            : 'bg-gradient-to-br from-gray-500/30 to-gray-600/20 text-gray-300 border-2 border-gray-400/50 shadow-gray-500/25'
                    }`} style={{ animationDelay: '0.3s' }}>
                        {selectedTicket.status.toUpperCase()}
                    </span>
                </div>

                <div className="glass-card rounded-2xl flex-grow overflow-y-auto custom-scrollbar p-8 mb-8 animate-fadeIn border border-white/10" style={{ animationDelay: '0.4s' }}>
                    <div className="glass-card rounded-2xl p-8 mb-8 border-2 border-blue-500/30 bg-gradient-to-br from-blue-500/10 to-cyan-500/10 hover:border-blue-400/50 transition-all duration-500 hover:shadow-xl hover:shadow-blue-500/20 group relative overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-400/5 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-800"></div>
                        <div className="flex items-center gap-4 mb-6 relative z-10">
                            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500/30 to-cyan-500/20 border-2 border-blue-400/50 flex items-center justify-center transition-all duration-300 group-hover:scale-110 group-hover:rotate-3 shadow-lg">
                                <i className="fas fa-user text-blue-300 transition-colors duration-300 group-hover:text-blue-200"></i>
                            </div>
                            <div className="transition-all duration-300 group-hover:translate-x-1">
                                <p className="text-white font-bold text-lg transition-colors duration-300 group-hover:text-blue-100">{selectedTicket.userName}</p>
                                <p className="text-gray-400 text-sm transition-colors duration-300 group-hover:text-gray-300">opened on {selectedTicket.createdAt || 'N/A'}</p>
                            </div>
                        </div>
                        <p className="text-gray-200 mb-6 leading-relaxed text-lg transition-colors duration-300 group-hover:text-gray-100 relative z-10">{selectedTicket.description}</p>
                        
                        <div className="flex flex-wrap gap-6 text-sm relative z-10">
                            {selectedTicket.department && (
                                <div className="flex items-center gap-3 glass-card px-4 py-2 rounded-xl transition-all duration-300 hover:scale-105 hover:bg-white/10 border border-white/10">
                                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-500/30 flex items-center justify-center">
                                        <i className="fas fa-building text-green-400 text-sm"></i>
                                    </div>
                                    <span className="text-gray-300 font-medium">{selectedTicket.department}</span>
                                </div>
                            )}
                            {selectedTicket.scriptName && (
                                <div className="flex items-center gap-3 glass-card px-4 py-2 rounded-xl transition-all duration-300 hover:scale-105 hover:bg-white/10 border border-white/10">
                                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/30 flex items-center justify-center">
                                        <i className="fas fa-code text-purple-400 text-sm"></i>
                                    </div>
                                    <span className="text-gray-300 font-medium">{selectedTicket.scriptName}</span>
                                </div>
                            )}
                            {selectedTicket.discordLink && (
                                <div className="flex items-center gap-3 glass-card px-4 py-2 rounded-xl transition-all duration-300 hover:scale-105 hover:bg-white/10 border border-white/10">
                                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500/20 to-blue-500/20 border border-indigo-500/30 flex items-center justify-center">
                                        <i className="fab fa-discord text-indigo-400 text-sm"></i>
                                    </div>
                                    <a href={selectedTicket.discordLink} target="_blank" rel="noopener noreferrer" className="text-indigo-400 hover:text-indigo-300 transition-colors font-medium">
                                        Discord Link <i className="fas fa-external-link-alt text-xs ml-1"></i>
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="space-y-6">
                        {selectedTicket.messages && selectedTicket.messages.map((message, index) => (
                            <div key={message.id} className={`flex animate-fadeIn ${message.senderId === user.uid ? 'justify-end' : 'justify-start'}`} style={{ animationDelay: `${index * 0.1}s` }}>
                                <div className={`max-w-[85%] ${message.senderId === user.uid ? 'order-1' : 'order-2'}`}>
                                    <div className={`glass-card p-6 rounded-2xl transition-all duration-500 hover:scale-[1.02] hover:-translate-y-1 hover:shadow-xl relative overflow-hidden group border-2 ${
                                        message.senderId === user.uid 
                                            ? 'bg-gradient-to-br from-blue-500/30 to-cyan-500/20 border-blue-400/50 hover:shadow-blue-500/25' 
                                            : 'bg-white/5 border-white/20 hover:border-white/30 hover:shadow-white/10'
                                    }`}>
                                        <div className={`absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700`}></div>
                                        <div className="flex items-center justify-between mb-3 relative z-10">
                                            <div className="flex items-center gap-3">
                                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-300 group-hover:scale-110 ${
                                                    message.senderId === user.uid
                                                        ? 'bg-gradient-to-br from-blue-500/30 to-cyan-500/20 border border-blue-400/50'
                                                        : 'bg-gradient-to-br from-gray-500/30 to-gray-600/20 border border-gray-400/50'
                                                }`}>
                                                    <i className={`fas fa-user text-sm ${
                                                        message.senderId === user.uid ? 'text-blue-300' : 'text-gray-300'
                                                    }`}></i>
                                                </div>
                                                <span className="text-white font-bold text-sm transition-colors duration-300 group-hover:text-gray-100">{message.senderName}</span>
                                            </div>
                                            <span className="text-gray-400 text-xs font-medium transition-colors duration-300 group-hover:text-gray-300">{message.timestamp}</span>
                                        </div>
                                        <p className="text-gray-200 leading-relaxed text-base transition-colors duration-300 group-hover:text-gray-100 relative z-10">{message.text}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                        <div ref={messagesEndRef} />
                    </div>
                </div>

                {selectedTicket.status === 'open' && (
                    <div className="glass-card rounded-2xl p-6 flex items-end gap-6 animate-fadeIn border-2 border-blue-500/30 bg-gradient-to-br from-blue-500/5 to-cyan-500/5 hover:border-blue-400/50 transition-all duration-500 hover:shadow-xl hover:shadow-blue-500/20 group relative overflow-hidden" style={{ animationDelay: '0.5s' }}>
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-400/3 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
                        <div className="flex-1 relative z-10">
                            <textarea
                                className="w-full glass-card backdrop-blur-xl bg-white/5 border-2 border-white/20 rounded-2xl p-6 text-white placeholder-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400/50 transition-all duration-500 focus:scale-[1.01] focus:shadow-xl focus:shadow-blue-500/20 hover:border-white/30 text-lg"
                                rows="3"
                                placeholder="Type your message here..."
                                value={newMessage}
                                onChange={handleTextareaChange}
                            />
                        </div>
                        <button
                            onClick={handleLocalSendMessage}
                            className="glass-card backdrop-blur-xl border-2 border-blue-400/60 px-8 py-6 rounded-2xl font-bold text-white shadow-xl hover:shadow-2xl hover:shadow-blue-500/25 transition-all duration-500 hover:scale-110 hover:-translate-y-2 relative overflow-hidden group/btn bg-gradient-to-br from-blue-500/40 to-cyan-500/30"
                        >
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent translate-x-[-100%] group-hover/btn:translate-x-[100%] transition-transform duration-1000"></div>
                            <div className="relative z-10 flex items-center">
                                <i className="fas fa-paper-plane mr-3 transition-all duration-300 group-hover/btn:rotate-12 group-hover/btn:scale-110"></i>
                                Send
                            </div>
                        </button>
                    </div>
                )}
                {selectedTicket.status === 'closed' && (
                    <div className="glass-card rounded-2xl p-8 text-center animate-fadeIn border-2 border-gray-500/30 bg-gradient-to-br from-gray-500/10 to-gray-600/10 hover:border-gray-400/50 transition-all duration-500 hover:shadow-xl hover:shadow-gray-500/20 group relative overflow-hidden" style={{ animationDelay: '0.5s' }}>
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-gray-400/3 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
                        <div className="relative z-10">
                            <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-gray-500/30 to-gray-600/20 border-2 border-gray-400/50 flex items-center justify-center animate-float">
                                <i className="fas fa-lock text-3xl text-gray-400"></i>
                            </div>
                            <p className="text-gray-300 font-bold text-xl mb-3 transition-colors duration-300 group-hover:text-gray-200">This ticket is closed</p>
                            <p className="text-gray-500 text-lg transition-colors duration-300 group-hover:text-gray-400">You can no longer send messages to this conversation</p>
                        </div>
                    </div>
                )}
            </div>
        );
    });

    // NewTicketModal component removed - now handled by App.js

    return (
        <div className="h-full flex flex-col">
            {selectedTicket ? (
                <TicketDetail
                    key={selectedTicket.id}
                    selectedTicket={selectedTicket}
                    user={user}
                    handleSendMessage={handleSendMessage}
                    messagesEndRef={messagesEndRef}
                    handleBackToTickets={handleBackToTickets} // Pass the new handler
                />
            ) : (
                <TicketList />
            )}
        </div>
    );
};

export default ClientSupport;